/**
 * Lazy Loading Components and Wrappers
 * Optimized lazy loading with suspense boundaries and error handling
 */

import React, { Suspense, ErrorInfo, ReactNode } from 'react';
import { Skeleton } from '../ui/skeleton';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { RefreshCw, AlertTriangle, Loader2 } from 'lucide-react';
import { createLazyComponent } from '../../utils/performance';

// Loading fallback components
export const PageSkeleton: React.FC = () => (
  <div className="flex-1 space-y-6 p-6">
    <div className="space-y-2">
      <Skeleton className="h-8 w-[200px]" />
      <Skeleton className="h-4 w-[300px]" />
    </div>
    
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
      {Array.from({ length: 6 }).map((_, i) => (
        <Card key={i}>
          <CardContent className="p-6">
            <Skeleton className="h-6 w-full mb-4" />
            <Skeleton className="h-4 w-2/3 mb-2" />
            <Skeleton className="h-4 w-1/2" />
          </CardContent>
        </Card>
      ))}
    </div>
  </div>
);

export const TableSkeleton: React.FC = () => (
  <div className="space-y-4">
    <div className="flex justify-between items-center">
      <Skeleton className="h-8 w-[200px]" />
      <div className="flex space-x-2">
        <Skeleton className="h-10 w-[120px]" />
        <Skeleton className="h-10 w-[100px]" />
      </div>
    </div>
    
    <Card>
      <CardContent className="p-6">
        <div className="space-y-4">
          {Array.from({ length: 8 }).map((_, i) => (
            <div key={i} className="flex items-center space-x-4">
              <Skeleton className="h-4 w-[100px]" />
              <Skeleton className="h-4 w-[150px]" />
              <Skeleton className="h-4 w-[120px]" />
              <Skeleton className="h-4 w-[80px]" />
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  </div>
);

export const ChartSkeleton: React.FC = () => (
  <Card>
    <CardContent className="p-6">
      <div className="space-y-4">
        <Skeleton className="h-6 w-[180px]" />
        <div className="h-[300px] w-full bg-muted rounded-lg flex items-center justify-center">
          <div className="flex items-center space-x-2 text-muted-foreground">
            <Loader2 className="h-6 w-6 animate-spin" />
            <span>Loading chart...</span>
          </div>
        </div>
      </div>
    </CardContent>
  </Card>
);

// Enhanced loading component with progress indication
interface LoadingFallbackProps {
  type?: 'page' | 'table' | 'chart' | 'component';
  message?: string;
  showProgress?: boolean;
}

export const LoadingFallback: React.FC<LoadingFallbackProps> = ({ 
  type = 'component', 
  message,
  showProgress = false 
}) => {
  const [progress, setProgress] = React.useState(0);

  React.useEffect(() => {
    if (showProgress) {
      const interval = setInterval(() => {
        setProgress((prev) => {
          if (prev >= 90) return prev;
          return prev + Math.random() * 15;
        });
      }, 200);

      return () => clearInterval(interval);
    }
  }, [showProgress]);

  switch (type) {
    case 'page':
      return <PageSkeleton />;
    case 'table':
      return <TableSkeleton />;
    case 'chart':
      return <ChartSkeleton />;
    default:
      return (
        <Card>
          <CardContent className="p-6 flex flex-col items-center justify-center min-h-[200px] space-y-4">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
            <div className="text-center space-y-2">
              <p className="text-sm font-medium">
                {message || 'Loading component...'}
              </p>
              {showProgress && (
                <div className="w-48 bg-muted rounded-full h-2">
                  <div 
                    className="bg-primary h-2 rounded-full transition-all duration-300"
                    style={{ width: `${progress}%` }}
                  />
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      );
  }
};

// Error boundary for lazy components
interface LazyErrorBoundaryState {
  hasError: boolean;
  error?: Error;
}

interface LazyErrorBoundaryProps {
  children: ReactNode;
  fallback?: React.ComponentType<{ error: Error; retry: () => void }>;
  componentName?: string;
}

class LazyErrorBoundary extends React.Component<LazyErrorBoundaryProps, LazyErrorBoundaryState> {
  constructor(props: LazyErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): LazyErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error(`❌ Lazy loading error in ${this.props.componentName || 'component'}:`, error, errorInfo);
    
    // Track error in performance monitor
    if (typeof window !== 'undefined') {
      (window as any).performanceMonitor?.metrics.set(
        `lazy_error_${this.props.componentName || 'component'}`, 
        Date.now()
      );
    }
  }

  handleRetry = () => {
    this.setState({ hasError: false, error: undefined });
  };

  render() {
    if (this.state.hasError && this.state.error) {
      if (this.props.fallback) {
        const FallbackComponent = this.props.fallback;
        return <FallbackComponent error={this.state.error} retry={this.handleRetry} />;
      }

      return (
        <Card className="border-destructive">
          <CardContent className="p-6 flex flex-col items-center justify-center min-h-[200px] space-y-4">
            <AlertTriangle className="h-12 w-12 text-destructive" />
            <div className="text-center space-y-2">
              <h3 className="font-semibold text-destructive">
                Failed to load {this.props.componentName || 'component'}
              </h3>
              <p className="text-sm text-muted-foreground">
                {this.state.error.message}
              </p>
              <Button 
                variant="outline" 
                size="sm" 
                onClick={this.handleRetry}
                className="mt-4"
              >
                <RefreshCw className="h-4 w-4 mr-2" />
                Try Again
              </Button>
            </div>
          </CardContent>
        </Card>
      );
    }

    return this.props.children;
  }
}

// HOC for lazy loading with error boundary and suspense
interface WithLazyLoadingOptions {
  fallback?: React.ComponentType<LoadingFallbackProps>;
  errorFallback?: React.ComponentType<{ error: Error; retry: () => void }>;
  componentName?: string;
  loadingType?: 'page' | 'table' | 'chart' | 'component';
  showProgress?: boolean;
}

export function withLazyLoading<P extends {}>(
  LazyComponent: React.LazyExoticComponent<React.ComponentType<P>>,
  options: WithLazyLoadingOptions = {}
) {
  const {
    fallback: CustomFallback,
    errorFallback,
    componentName,
    loadingType = 'component',
    showProgress = false
  } = options;

  return React.forwardRef<any, P>((props, ref) => {
    const FallbackComponent = CustomFallback || LoadingFallback;

    return (
      <LazyErrorBoundary 
        fallback={errorFallback} 
        componentName={componentName}
      >
        <Suspense 
          fallback={
            <FallbackComponent 
              type={loadingType} 
              message={`Loading ${componentName}...`}
              showProgress={showProgress}
            />
          }
        >
          <LazyComponent {...props} ref={ref} />
        </Suspense>
      </LazyErrorBoundary>
    );
  });
}

// Lazy image component with intersection observer
interface LazyImageProps {
  src: string;
  alt: string;
  className?: string;
  placeholder?: string;
  threshold?: number;
  onLoad?: () => void;
  onError?: () => void;
}

export const LazyImage: React.FC<LazyImageProps> = ({
  src,
  alt,
  className,
  placeholder = '/placeholder.jpg',
  threshold = 0.1,
  onLoad,
  onError
}) => {
  const [isLoaded, setIsLoaded] = React.useState(false);
  const [isInView, setIsInView] = React.useState(false);
  const [hasError, setHasError] = React.useState(false);
  const imgRef = React.useRef<HTMLImageElement>(null);

  React.useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsInView(true);
          observer.disconnect();
        }
      },
      { threshold }
    );

    if (imgRef.current) {
      observer.observe(imgRef.current);
    }

    return () => observer.disconnect();
  }, [threshold]);

  React.useEffect(() => {
    if (isInView && src && !hasError) {
      const img = new Image();
      img.onload = () => {
        setIsLoaded(true);
        onLoad?.();
      };
      img.onerror = () => {
        setHasError(true);
        onError?.();
      };
      img.src = src;
    }
  }, [isInView, src, hasError, onLoad, onError]);

  return (
    <img
      ref={imgRef}
      src={isLoaded ? src : placeholder}
      alt={alt}
      className={className}
      loading="lazy"
      style={{
        opacity: isLoaded ? 1 : 0.7,
        transition: 'opacity 0.3s ease'
      }}
    />
  );
};

export default LazyErrorBoundary;